import { DateInput, Select } from '@orbind/ui'
import type { SavedFilter } from '@orbind/core'
import { useEffect, useRef, useState } from 'react'
import './periodo-de-datas.css'

/**
 * PERÍODO por data, num controle só (SPEC-20261104-2017, pedido do Cristian:
 * "isso eu quero em um único campo e fica nos quadros e nos itens de
 * trabalho"). Um botão "Período" abre o painel com a DATA (conclusão, criação,
 * prazo, início), os ATALHOS (hoje, semana, mês, 7/30 dias) e o personalizado
 * com início e fim opcionais. Vira um critério `between` por cima dos outros
 * filtros; vale só na sessão.
 */

export type PresetDePeriodo = '' | 'hoje' | 'semana' | 'mes' | '7d' | '30d' | 'custom'
export type CampoDePeriodo = 'completedAt' | 'createdAt' | 'dueAt' | 'startAt'
export type Periodo = { campo: CampoDePeriodo; preset: PresetDePeriodo; de: string; ate: string }

export const PERIODO_VAZIO: Periodo = { campo: 'completedAt', preset: '', de: '', ate: '' }

export const CAMPOS_DE_PERIODO: { value: CampoDePeriodo; label: string; curto: string }[] = [
  { value: 'completedAt', label: 'Data de conclusão', curto: 'Conclusão' },
  { value: 'createdAt', label: 'Data de criação', curto: 'Criação' },
  { value: 'dueAt', label: 'Prazo', curto: 'Prazo' },
  { value: 'startAt', label: 'Início', curto: 'Início' },
]

export const PRESETS_DE_PERIODO: { value: PresetDePeriodo; label: string }[] = [
  { value: '', label: 'Todos' },
  { value: 'hoje', label: 'Hoje' },
  { value: 'semana', label: 'Esta semana' },
  { value: 'mes', label: 'Este mês' },
  { value: '7d', label: 'Últimos 7 dias' },
  { value: '30d', label: 'Últimos 30 dias' },
  { value: 'custom', label: 'Personalizado' },
]

/** Data LOCAL como AAAA-MM-DD (sem o salto de fuso do toISOString). */
export const ymd = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

/** AAAA-MM-DD → Date local (meia-noite), ou null quando vazio. */
export const dataLocal = (texto: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texto)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null
}

/** Início e fim (AAAA-MM-DD) de um atalho, a partir de hoje; vazio = sem o lado. */
export function limitesDoPreset(preset: PresetDePeriodo, de: string, ate: string, hoje = new Date()): [string, string] | null {
  const dia = (n: number) => ymd(new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + n))
  switch (preset) {
    case 'hoje':
      return [dia(0), dia(0)]
    case 'semana': {
      // semana de segunda a domingo
      const dow = (hoje.getDay() + 6) % 7
      return [dia(-dow), dia(6 - dow)]
    }
    case 'mes':
      return [ymd(new Date(hoje.getFullYear(), hoje.getMonth(), 1)), ymd(new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0))]
    case '7d':
      return [dia(-6), dia(0)]
    case '30d':
      return [dia(-29), dia(0)]
    case 'custom':
      return de || ate ? [de, ate] : null
    default:
      return null
  }
}

/** O período como filtro de um critério "entre" (lados opcionais), ou null. */
export function filtroDoPeriodo(p: Periodo): SavedFilter | null {
  const limites = limitesDoPreset(p.preset, p.de, p.ate)
  if (!limites) return null
  return { id: 'periodo', name: 'Período', join: 'AND', criteria: [{ id: 'periodo', field: p.campo, operator: 'between', value: limites }] }
}

const br = (iso: string) => {
  const d = dataLocal(iso)
  return d ? d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''
}

/** Rótulo curto do botão: "Período" ou "Conclusão · Este mês" / "Criação · 01/11 – 15/11". */
export function rotuloDoPeriodo(p: Periodo): string {
  if (!filtroDoPeriodo(p)) return 'Período'
  const campo = CAMPOS_DE_PERIODO.find((c) => c.value === p.campo)?.curto ?? ''
  if (p.preset === 'custom') {
    const de = br(p.de)
    const ate = br(p.ate)
    return `${campo} · ${de && ate ? `${de} – ${ate}` : de ? `a partir de ${de}` : `até ${ate}`}`
  }
  return `${campo} · ${PRESETS_DE_PERIODO.find((x) => x.value === p.preset)?.label ?? ''}`
}

export function PeriodoDeDatas({ value, onChange }: { value: Periodo; onChange: (p: Periodo) => void }) {
  const [aberto, setAberto] = useState(false)
  const raiz = useRef<HTMLDivElement>(null)
  const ligado = filtroDoPeriodo(value) !== null

  // fecha ao clicar fora ou com Esc
  useEffect(() => {
    if (!aberto) return
    const fora = (e: MouseEvent) => {
      if (!raiz.current?.contains(e.target as Node)) setAberto(false)
    }
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false)
    }
    document.addEventListener('mousedown', fora)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', fora)
      document.removeEventListener('keydown', esc)
    }
  }, [aberto])

  return (
    <div className="periodo" ref={raiz}>
      <button
        type="button"
        className={`periodo__botao${ligado ? ' periodo__botao--on' : ''}`}
        aria-haspopup="dialog"
        aria-expanded={aberto}
        title="Período por data (só nesta sessão)"
        onClick={() => setAberto((v) => !v)}
      >
        <i className="far fa-calendar-alt" aria-hidden />
        <span className="periodo__rotulo">{rotuloDoPeriodo(value)}</span>
        <i className="fas fa-chevron-down periodo__seta" aria-hidden />
      </button>
      {aberto ? (
        <div className="periodo__painel" role="dialog" aria-label="Período por data">
          <label className="periodo__campo">
            <span>Data</span>
            <Select
              value={value.campo}
              options={CAMPOS_DE_PERIODO.map((c) => ({ value: c.value, label: c.label }))}
              onChange={(v) => onChange({ ...value, campo: v as CampoDePeriodo })}
            />
          </label>
          <div className="periodo__atalhos" role="radiogroup" aria-label="Intervalo">
            {PRESETS_DE_PERIODO.map((p) => (
              <button
                key={p.value || 'todos'}
                type="button"
                role="radio"
                aria-checked={value.preset === p.value}
                className={`periodo__atalho${value.preset === p.value ? ' periodo__atalho--on' : ''}`}
                onClick={() => {
                  onChange({ ...value, preset: p.value })
                  if (p.value !== 'custom') setAberto(false)
                }}
              >
                {p.label}
              </button>
            ))}
          </div>
          {value.preset === 'custom' ? (
            <div className="periodo__datas">
              <label className="periodo__campo">
                <span>Início (opcional)</span>
                <DateInput value={dataLocal(value.de)} placeholder="início" onChange={(d) => onChange({ ...value, de: d ? ymd(d) : '' })} />
              </label>
              <label className="periodo__campo">
                <span>Fim (opcional)</span>
                <DateInput value={dataLocal(value.ate)} placeholder="fim" onChange={(d) => onChange({ ...value, ate: d ? ymd(d) : '' })} />
              </label>
            </div>
          ) : null}
          {ligado ? (
            <button type="button" className="periodo__limpar" onClick={() => { onChange({ ...value, preset: '', de: '', ate: '' }); setAberto(false) }}>
              <i className="fas fa-times" aria-hidden /> Limpar período
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
